import { useMemo, useState } from 'react';
import {
  buildShoe,
  countDelta,
  dealerPlay,
  drawN,
  handTotal,
  hiLoValue,
  isBlackjack,
  isBust,
  isPair,
} from '../game/engine.js';
import { basicStrategyAction, insuranceRecommendation, reasonFor } from '../game/basicStrategy.js';
import { Hand } from '../components/Card.jsx';
import { CoachPanel } from '../components/CoachPanel.jsx';

const PHASES = {
  BETTING: 'betting',
  INSURANCE: 'insurance',
  PLAYER: 'player',
  DEALER: 'dealer',
  SETTLED: 'settled',
};

function newHand(cards, bet, opts = {}) {
  return {
    cards,
    bet,
    doubled: false,
    busted: false,
    surrendered: false,
    isSplitAce: opts.isSplitAce || false,
    splitFromAce: opts.splitFromAce || false,
    finished: false,
    net: null,
  };
}

export function TableMode({ rules, prefs, bankroll, setBankroll, stats, setStats }) {
  const [shoe, setShoe] = useState(() => buildShoe(rules.decks));
  const [shoeSize, setShoeSize] = useState(rules.decks * 52);
  const [runningCount, setRunningCount] = useState(0);

  const [bet, setBet] = useState(25);
  const [phase, setPhase] = useState(PHASES.BETTING);

  const [hands, setHands] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [dealer, setDealer] = useState([]);
  const [revealHole, setRevealHole] = useState(false);
  const [insurance, setInsurance] = useState(0);

  const [coach, setCoach] = useState(null);
  const [message, setMessage] = useState('Place a bet and click Deal.');

  const cardsDealt = shoeSize - shoe.length;
  const decksRemaining = Math.max(0.25, shoe.length / 52);
  const trueCount = useMemo(() => runningCount / decksRemaining, [runningCount, decksRemaining]);
  const needsReshuffle = cardsDealt / shoeSize >= rules.penetration;

  const active = hands[activeIdx];
  const dealerUpRank = dealer[0]?.rank;
  const playerTotal = active ? handTotal(active.cards).total : 0;
  const playerSoft = active ? handTotal(active.cards).soft : false;
  const dealerVisibleTotal = revealHole ? handTotal(dealer).total : (dealer[0] ? handTotal([dealer[0]]).total : 0);

  const canDouble = phase === PHASES.PLAYER && active && active.cards.length === 2 && !active.isSplitAce && bankroll >= active.bet && (hands.length === 1 || rules.doubleAfterSplit);
  const canSplit = phase === PHASES.PLAYER && active && active.cards.length === 2 && isPair(active.cards) && hands.length < rules.maxSplitHands && bankroll >= active.bet;
  const canSurrender = phase === PHASES.PLAYER && active && active.cards.length === 2 && hands.length === 1 && rules.surrender === 'late';

  function applyDraw(currentShoe, n) {
    const { cards, shoe: next } = drawN(currentShoe, n);
    setRunningCount((rc) => rc + countDelta(cards));
    return { cards, next };
  }

  // Reveal the dealer's hole card AND add its Hi-Lo value to the count.
  // Called once per hand, on whichever path actually shows the hole.
  function revealHoleAndCount() {
    setRevealHole(true);
    if (dealer[1]) setRunningCount((rc) => rc + hiLoValue(dealer[1].rank));
  }

  function reshuffleIfNeeded() {
    if (!needsReshuffle) return shoe;
    const fresh = buildShoe(rules.decks);
    setShoeSize(fresh.length);
    setRunningCount(0);
    setMessage('Shoe reshuffled.');
    setShoe(fresh);
    return fresh;
  }

  function handleDeal() {
    if (bet <= 0 || bet > bankroll) {
      setMessage('Bet must be between 1 and your bankroll.');
      return;
    }
    const startShoe = reshuffleIfNeeded();
    let s = startShoe;
    // Count the 2 player cards + dealer upcard. Hole card is added at reveal.
    const p = applyDraw(s, 2); s = p.next;
    const upDraw = applyDraw(s, 1); s = upDraw.next;
    const holeDraw = drawN(s, 1); s = holeDraw.shoe;
    const dealerCards = [upDraw.cards[0], holeDraw.cards[0]];
    setShoe(s);
    const initialHand = newHand(p.cards, bet);
    setHands([initialHand]);
    setActiveIdx(0);
    setDealer(dealerCards);
    setRevealHole(false);
    setInsurance(0);
    setCoach(null);
    setMessage('');

    const playerBJ = isBlackjack(p.cards);
    const dealerBJ = isBlackjack(dealerCards);

    // Insurance offered if dealer shows ace (regardless of player BJ — even money is the same EV).
    if (rules.insuranceAllowed && dealerCards[0].rank === 'A' && !playerBJ) {
      setPhase(PHASES.INSURANCE);
      setMessage('Dealer shows an ace — insurance?');
      return;
    }

    if (playerBJ || dealerBJ) {
      settleNaturals(p.cards, dealerCards, bet);
      return;
    }
    setPhase(PHASES.PLAYER);
  }

  function settleNaturals(playerCards, dealerCards, originalBet, insuranceBet = 0) {
    revealHoleAndCount();
    const playerBJ = isBlackjack(playerCards);
    const dealerBJ = isBlackjack(dealerCards);
    let net = 0;
    let msg;
    if (playerBJ && dealerBJ) {
      msg = 'Both have blackjack — push.';
      bumpStats({ hands: 1, pushes: 1 });
    } else if (playerBJ) {
      net = originalBet * rules.blackjackPays;
      msg = `Blackjack! +$${net.toFixed(2)}`;
      bumpStats({ hands: 1, wins: 1, blackjacks: 1 });
    } else { // dealerBJ
      net = -originalBet;
      msg = `Dealer blackjack. -$${originalBet}`;
      bumpStats({ hands: 1 });
    }
    // Insurance settles independently
    if (insuranceBet > 0) {
      if (dealerBJ) {
        net += insuranceBet * 2;
        msg += ` Insurance won +$${(insuranceBet * 2).toFixed(2)}.`;
      } else {
        net -= insuranceBet;
        msg += ` Insurance lost -$${insuranceBet}.`;
      }
    }
    setBankroll((b) => b + net);
    setMessage(msg);
    setPhase(PHASES.SETTLED);
  }

  function bumpStats(delta) {
    setStats((st) => {
      const next = { ...st };
      for (const k of Object.keys(delta)) next[k] = (next[k] || 0) + delta[k];
      return next;
    });
  }

  function recordDecision(taken, opts = {}) {
    const suggested = basicStrategyAction(active.cards, dealerUpRank, {
      canDouble, canSurrender, canSplit, das: rules.doubleAfterSplit,
      ...opts,
    });
    const correct = taken === suggested;
    if (prefs.coachOn) {
      setCoach({ correct, suggested, taken, reason: reasonFor(active.cards, dealerUpRank, suggested) });
    }
    setStats((st) => ({ ...st, decisions: (st.decisions || 0) + 1, correct: (st.correct || 0) + (correct ? 1 : 0) }));
  }

  function recordInsuranceDecision(taken) {
    const rec = insuranceRecommendation(trueCount);
    const optimal = rec.take ? 'take' : 'decline';
    const correct = taken === optimal;
    if (prefs.coachOn) {
      setCoach({ correct, suggested: optimal, taken, reason: rec.reason });
    }
    setStats((st) => ({ ...st, decisions: (st.decisions || 0) + 1, correct: (st.correct || 0) + (correct ? 1 : 0) }));
  }

  function handleInsurance(take) {
    recordInsuranceDecision(take ? 'take' : 'decline');
    const insBet = take ? bet / 2 : 0;
    setInsurance(insBet);
    const dealerBJ = isBlackjack(dealer);
    if (dealerBJ) {
      settleNaturals(hands[0].cards, dealer, bet, insBet);
      return;
    }
    // Dealer doesn't have BJ → insurance loses if taken, then play normally.
    if (insBet > 0) {
      setBankroll((b) => b - insBet);
      setMessage(`Insurance lost -$${insBet}.`);
    } else {
      setMessage('');
    }
    setPhase(PHASES.PLAYER);
  }

  function updateActive(updater) {
    setHands((hs) => hs.map((h, i) => (i === activeIdx ? updater(h) : h)));
  }

  function advanceOrFinish(updatedHands) {
    // Find next unfinished hand starting from activeIdx + 1.
    let next = activeIdx + 1;
    while (next < updatedHands.length && updatedHands[next].finished) next++;
    if (next < updatedHands.length) {
      setActiveIdx(next);
      // Split-aces auto-finish — also auto-skip in the recursion if needed.
      if (updatedHands[next].isSplitAce) {
        // Already 1-card hand drawn at split; just mark finished.
        const finished = updatedHands.map((h, i) => (i === next ? { ...h, finished: true } : h));
        setHands(finished);
        advanceOrFinish(finished);
      }
    } else {
      runDealerAndSettle(updatedHands);
    }
  }

  function handleHit() {
    if (phase !== PHASES.PLAYER || !active) return;
    recordDecision('hit');
    const { cards: drawn, next } = applyDraw(shoe, 1);
    setShoe(next);
    const newCards = [...active.cards, drawn[0]];
    const busted = isBust(newCards);
    const updatedHands = hands.map((h, i) =>
      i === activeIdx ? { ...h, cards: newCards, busted, finished: busted } : h
    );
    setHands(updatedHands);
    if (busted) advanceOrFinish(updatedHands);
  }

  function handleStand() {
    if (phase !== PHASES.PLAYER || !active) return;
    recordDecision('stand');
    const updatedHands = hands.map((h, i) => i === activeIdx ? { ...h, finished: true } : h);
    setHands(updatedHands);
    advanceOrFinish(updatedHands);
  }

  function handleDouble() {
    if (!canDouble) return;
    recordDecision('double');
    const { cards: drawn, next } = applyDraw(shoe, 1);
    setShoe(next);
    const newCards = [...active.cards, drawn[0]];
    const busted = isBust(newCards);
    const updatedHands = hands.map((h, i) =>
      i === activeIdx ? { ...h, cards: newCards, doubled: true, bet: h.bet * 2, busted, finished: true } : h
    );
    setHands(updatedHands);
    advanceOrFinish(updatedHands);
  }

  function handleSurrender() {
    if (!canSurrender) return;
    recordDecision('surrender');
    revealHoleAndCount();
    const loss = active.bet / 2;
    setBankroll((b) => b - loss);
    setMessage(`Surrendered. -$${loss}`);
    bumpStats({ hands: 1, surrenders: 1 });
    setHands(hands.map((h, i) => i === activeIdx ? { ...h, surrendered: true, finished: true } : h));
    setPhase(PHASES.SETTLED);
  }

  function handleSplit() {
    if (!canSplit) return;
    recordDecision('split');
    const [c1, c2] = active.cards;
    const isAce = c1.rank === 'A';
    const { cards: drawn, next } = applyDraw(shoe, 2);
    setShoe(next);
    const handA = newHand([c1, drawn[0]], active.bet, { isSplitAce: isAce, splitFromAce: isAce });
    const handB = newHand([c2, drawn[1]], active.bet, { isSplitAce: isAce, splitFromAce: isAce });
    if (isAce) {
      handA.finished = true;
      handB.finished = true;
    }
    const updatedHands = [
      ...hands.slice(0, activeIdx),
      handA,
      handB,
      ...hands.slice(activeIdx + 1),
    ];
    setHands(updatedHands);
    if (isAce) {
      advanceOrFinish(updatedHands);
    }
    // For non-ace splits, stay on activeIdx (now hand A) and let player act.
  }

  function runDealerAndSettle(finalHands) {
    setPhase(PHASES.DEALER);
    revealHoleAndCount();
    // If all hands lost, dealer doesn't draw — they already won.
    const allDead = finalHands.every((h) => h.busted || h.surrendered);
    let finalDealer = dealer;
    let nextShoe = shoe;
    if (!allDead) {
      const result = dealerPlay(dealer, shoe, rules);
      finalDealer = result.dealer;
      nextShoe = result.shoe;
      if (result.drawn.length > 0) {
        setRunningCount((rc) => rc + countDelta(result.drawn));
      }
    }
    setShoe(nextShoe);
    setDealer(finalDealer);

    // Settle each hand.
    let totalNet = 0;
    let wins = 0, pushes = 0;
    const settledHands = finalHands.map((h) => {
      if (h.surrendered) return { ...h, net: -h.bet / 2 };
      if (h.busted) {
        return { ...h, net: -h.bet };
      }
      const p = handTotal(h.cards).total;
      const d = handTotal(finalDealer).total;
      let net;
      if (d > 21) net = h.bet;
      else if (p > d) net = h.bet;
      else if (p < d) net = -h.bet;
      else net = 0;
      if (net > 0) wins++;
      if (net === 0) pushes++;
      return { ...h, net };
    });
    totalNet = settledHands.reduce((acc, h) => acc + h.net, 0);
    setHands(settledHands);
    setBankroll((b) => b + totalNet);

    const handsCount = settledHands.length;
    bumpStats({ hands: handsCount, wins, pushes });

    if (handsCount === 1) {
      const n = settledHands[0].net;
      if (n > 0) setMessage(`You win +$${n.toFixed(2)}`);
      else if (n < 0) setMessage(`Dealer wins. -$${Math.abs(n).toFixed(2)}`);
      else setMessage('Push.');
    } else {
      setMessage(`${handsCount} hands settled. Net ${totalNet >= 0 ? '+' : '-'}$${Math.abs(totalNet).toFixed(2)}.`);
    }
    setPhase(PHASES.SETTLED);
  }

  function handleNextHand() {
    setHands([]);
    setActiveIdx(0);
    setDealer([]);
    setRevealHole(false);
    setInsurance(0);
    setCoach(null);
    setPhase(PHASES.BETTING);
    setMessage('Place a bet and click Deal.');
  }

  return (
    <div className="flex flex-col items-center justify-center px-4 py-6 gap-6">
      {/* Bankroll/count strip */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm bg-black/20 rounded-lg px-4 py-2 border border-white/10">
        <Stat label="Bankroll" value={`$${Math.round(bankroll)}`} />
        <Stat label="Hands" value={stats.hands || 0} />
        <Stat label="Win rate" value={(stats.hands || 0) > 0 ? `${Math.round(((stats.wins || 0) / stats.hands) * 100)}%` : '—'} />
        <Stat label="Strategy accuracy" value={(stats.decisions || 0) > 0 ? `${Math.round(((stats.correct || 0) / stats.decisions) * 100)}%` : '—'} />
        {prefs.showCount && (
          <>
            <Stat label="RC" value={runningCount} />
            <Stat label="TC" value={trueCount.toFixed(1)} />
            <Stat label="Decks" value={decksRemaining.toFixed(1)} />
          </>
        )}
      </div>

      {/* Dealer */}
      <Section title="Dealer" total={revealHole ? handTotal(dealer).total : dealerVisibleTotal}>
        <Hand cards={dealer} hideSecond={!revealHole} />
      </Section>

      <div className="text-base italic text-white/80 min-h-[1.5rem] text-center">{message}</div>

      {/* Player hand(s) */}
      {hands.length === 0 ? (
        <Section title="You" total={0}><Hand cards={[]} /></Section>
      ) : (
        <div className="flex flex-wrap items-end justify-center gap-6">
          {hands.map((h, i) => (
            <PlayerHandView
              key={i}
              hand={h}
              isActive={i === activeIdx && phase === PHASES.PLAYER}
              label={hands.length > 1 ? `Hand ${i + 1}` : 'You'}
            />
          ))}
        </div>
      )}

      {phase === PHASES.BETTING && (
        <BetControls bet={bet} setBet={setBet} bankroll={bankroll} onDeal={handleDeal} />
      )}

      {phase === PHASES.INSURANCE && (
        <div className="flex flex-col items-center gap-3">
          <div className="text-white/70 text-sm">Insurance: pays 2:1 if dealer has blackjack. Costs ½ your bet.</div>
          <div className="flex gap-3">
            <ActionBtn onClick={() => handleInsurance(true)} label={`Take insurance ($${bet / 2})`} />
            <ActionBtn onClick={() => handleInsurance(false)} label="Decline" />
          </div>
        </div>
      )}

      {phase === PHASES.PLAYER && (
        <ActionControls
          onHit={handleHit}
          onStand={handleStand}
          onDouble={handleDouble}
          onSplit={handleSplit}
          onSurrender={handleSurrender}
          canDouble={canDouble}
          canSplit={canSplit}
          canSurrender={canSurrender}
        />
      )}

      {phase === PHASES.SETTLED && (
        <button
          onClick={handleNextHand}
          className="px-8 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition"
        >
          Next hand
        </button>
      )}

      {prefs.coachOn && coach && (
        <CoachPanel correct={coach.correct} taken={coach.taken} suggested={coach.suggested} reason={coach.reason} />
      )}

      <p className="text-xs text-white/40">
        {rules.decks}-deck · {rules.dealerHitsSoft17 ? 'H17' : 'S17'} · BJ {rules.blackjackPays === 1.5 ? '3:2' : '6:5'} ·
        {' '}{Math.round((1 - cardsDealt / shoeSize) * 100)}% shoe remaining
      </p>
    </div>
  );
}

function Section({ title, total, children }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-white/70 text-xs uppercase tracking-widest">
        {title}
        {total > 0 && <span className="ml-3 text-white text-lg font-semibold">{total}</span>}
      </div>
      {children}
    </div>
  );
}

function PlayerHandView({ hand, isActive, label }) {
  const { total, soft } = handTotal(hand.cards);
  const ringClass = isActive ? 'ring-4 ring-chip rounded-lg p-2' : 'p-2';
  const note = hand.busted ? 'BUST' :
               hand.surrendered ? 'SURRENDER' :
               hand.doubled ? 'DOUBLED' :
               hand.isSplitAce ? '21 (split A)' :
               total > 0 ? `${total}${soft ? ' soft' : ''}` : '';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-white/70 text-xs uppercase tracking-widest flex items-center gap-2">
        {label}
        {note && <span className="text-white text-sm font-semibold">{note}</span>}
      </div>
      <div className={ringClass}>
        <Hand cards={hand.cards} />
      </div>
      <div className="text-xs text-white/50">Bet: ${hand.bet}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-white/50 text-[10px] uppercase tracking-wider">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function BetControls({ bet, setBet, bankroll, onDeal }) {
  const chips = [5, 25, 100, 500];
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex gap-2 flex-wrap justify-center">
        {chips.map((c) => (
          <button key={c} onClick={() => setBet(Math.min(bankroll, bet + c))}
            className="px-3 py-2 rounded-full bg-chip/90 text-black font-bold text-sm hover:brightness-110">
            +${c}
          </button>
        ))}
        <button onClick={() => setBet(0)}
          className="px-3 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/20">clear</button>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-white/70 text-sm">Bet:</span>
        <input type="number" value={bet}
          onChange={(e) => setBet(Math.max(0, Math.min(bankroll, parseInt(e.target.value || 0, 10))))}
          className="w-24 px-3 py-2 rounded bg-white/10 border border-white/20 text-white text-center" />
        <button onClick={onDeal} disabled={bet <= 0 || bet > bankroll}
          className="px-8 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed">
          Deal
        </button>
      </div>
    </div>
  );
}

function ActionControls({ onHit, onStand, onDouble, onSplit, onSurrender, canDouble, canSplit, canSurrender }) {
  return (
    <div className="flex gap-2 flex-wrap justify-center">
      <ActionBtn onClick={onHit} label="Hit" />
      <ActionBtn onClick={onStand} label="Stand" />
      <ActionBtn onClick={onDouble} label="Double" disabled={!canDouble} />
      <ActionBtn onClick={onSplit} label="Split" disabled={!canSplit} />
      <ActionBtn onClick={onSurrender} label="Surrender" disabled={!canSurrender} />
    </div>
  );
}

function ActionBtn({ onClick, label, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="px-5 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition disabled:opacity-30 disabled:cursor-not-allowed border border-white/20">
      {label}
    </button>
  );
}
